import env from 'src/configs/env';

const slugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const allowedOrigins = new Set([
    'https://xprt.jobs',
    'https://localhost.xprt.jobs:9300'
]);

/**
 * Builds an Xprt Jobs category URL, returning nothing rather than throwing when the slugs or origin are not allowed.
 *
 * @param {string} classificationSlug - The classification slug for the category.
 * @param {string} [locationSlug] - The location slug for the category.
 * @param {string} [baseUrl] - The Xprt Jobs base URL to build against.
 * @returns {string|null} - The category URL, or null when it cannot be built safely.
 */
export function tryBuildXprtJobsCategoryUrl(
    classificationSlug,
    locationSlug = 'australia',
    baseUrl = env.xprtJobsBaseUrl
) {
    try {
        return buildXprtJobsCategoryUrl(classificationSlug, locationSlug, baseUrl);
    } catch {
        return null;
    }
}

/**
 * Builds an Xprt Jobs category URL from slugs that have been checked against the route slug pattern.
 *
 * @param {string} classificationSlug - The classification slug for the category.
 * @param {string} [locationSlug] - The location slug for the category.
 * @param {string} [baseUrl] - The Xprt Jobs base URL to build against.
 * @returns {string} - The category URL.
 * @throws {Error} - If either slug is not a route slug, or the base URL is not an allowed Xprt Jobs origin.
 */
export function buildXprtJobsCategoryUrl(
    classificationSlug,
    locationSlug = 'australia',
    baseUrl = env.xprtJobsBaseUrl
) {
    if (!isRouteSlug(classificationSlug) || !isRouteSlug(locationSlug)) {
        throw new Error('Invalid Xprt Jobs route slug');
    }
    const origin = getXprtJobsOrigin(baseUrl);
    return `${origin}/jobs/${classificationSlug}/${locationSlug}`;
}

function isRouteSlug(slug) {
    return typeof slug === 'string' && slugPattern.test(slug);
}

/**
 * Resolves the origin of an Xprt Jobs base URL, accepting only the origins the client may hand off to.
 *
 * @param {string} [baseUrl] - The Xprt Jobs base URL to read the origin from.
 * @returns {string} - The allowed origin.
 * @throws {Error} - If the base URL cannot be parsed, or its origin is not an allowed Xprt Jobs origin.
 */
export function getXprtJobsOrigin(baseUrl = env.xprtJobsBaseUrl) {
    let url;
    try {
        url = new URL(baseUrl);
    } catch {
        throw new Error('Invalid Xprt Jobs origin');
    }
    if (!allowedOrigins.has(url.origin)) {
        throw new Error('Invalid Xprt Jobs origin');
    }
    return url.origin;
}
